<template>
  <div class="flex flex-col justify-between items-center h-screen w-full">
    <HeaderBar />
    <div id="index" class="flex flex-col items-center w-full text-sm sm:text-base">
      <PageTitle :text="'Création d\'une nouvelle configuration'" />
      <PageDescription :text="this.description" />
      <div class="flex flex-row">
        <div class="mr-2 self-center">Largeur du dressing :</div>
        <input
          type="number"
          min="0"
          max="650"
          step="10"
          class="border-2 rounded border-accent text-right"
          placeholder="0"
          @input="launchPresetsCalculation($event.target.max, $event.target.value)"
        />
        <div class="ml-2 self-center">cm (max. 650cm)</div>
      </div>
      <div v-if="!this.hasAtLeastOneConfigurationDefined() && this.typedWidth <= 0" class="text-accent">Veuillez saisir une valeur pour commencer.</div>
      <div id="configuration-preset-picker" class="text-center" v-else-if="this.hasAtLeastOneConfigurationDefined()">
        <h2 class="text-accent">Voici des exemples de configurations pour une largeur disponible de {{ this.typedWidth }} cm:</h2>
        <div id="buttons-group" class="grid grid-cols-2 xl:grid-cols-4 gap-4 justify-items-center m-4">
          <PresetButton
            :buttonId="0"
            :chosen="selectedButtonId == 0"
            :presets="this.presets.closestWidthPresets"
            :icon="'closestWidth'"
            :description="'largeur la plus proche de celle donnée'"
            @button-selected="this.presetButtonSelected"
            @button-unselected="this.presetButtonUnselected"
          />
          <PresetButton
            :buttonId="1"
            :chosen="selectedButtonId == 1"
            :presets="this.presets.smallestNumberOfElementsPresets"
            :icon="'smallestNumberOfElements'"
            :description="'le moins d\'éléments possible'"
            @button-selected="this.presetButtonSelected"
            @button-unselected="this.presetButtonUnselected"
          />
          <PresetButton
            :buttonId="2"
            :chosen="selectedButtonId == 2"
            :presets="this.presets.largestNumberOfElementsPresets"
            :icon="'largestNumberOfElements'"
            :description="'le plus d\'élements possible'"
            @button-selected="this.presetButtonSelected"
            @button-unselected="this.presetButtonUnselected"
          />
          <PresetButton
            :buttonId="3"
            :chosen="selectedButtonId == 3"
            :presets="this.presets.lowestCostPresets"
            :icon="'lowestCost'"
            :description="'le moins cher'"
            @button-selected="this.presetButtonSelected"
            @button-unselected="this.presetButtonUnselected"
          />
        </div>
      </div>
      <div v-if="!this.hasAtLeastOneConfigurationDefined() && this.typedWidth > 0" class="text-accent">
        Aucune configuration trouvée pour une largeur disponible de {{ this.typedWidth }} cm.
      </div>
      <div class="flex flex-col lg:flex-row align-middle items-center gap-4 my-4" :class="this.hasAtLeastOneConfigurationDefined() ? 'mt-0' : ''">
        <!-- Use the selected configuration preset -->
        <GenericButton
          v-if="this.selectedButtonId < 0 || this.numberOfConfigurationsInPresetAtIndex() == 0"
          :text="'Utiliser la config. sélectionnée'"
          :icon="'x'"
          :isFilled="false"
          :isDisabled="true"
        />
        <router-link to="/configurator" v-else>
          <GenericButton
            :text="`Utiliser la config. ${this.selectedConfigurationId == -1 ? 'sélectionnée' : 'n°' + this.selectedConfigurationId}`"
            :icon="'check'"
            :isFilled="true"
            :isDisabled="false"
            @click="sendPresetConfiguration"
          />
        </router-link>
        <!-- Restore the last configuration -->
        <router-link to="/configurator" v-if="this.$store.state.currentConfiguration.length > 0">
          <GenericButton
            :text="`Reprendre dernière config. - ${calcConfigPrice(this.$store.state.currentConfiguration)}€`"
            :icon="'bookmark'"
            :isFilled="true"
          />
        </router-link>
        <GenericButton v-else :text="'Reprendre dernière config.'" :icon="'x'" :isFilled="false" :isDisabled="true" />
        <!-- Start from sratch -->
        <router-link to="/configurator" @click="this.$store.commit('clearCurrentConfiguration')">
          <GenericButton :text="'Nouvelle config.'" :icon="'fast-forward'" :isFilled="true" />
        </router-link>
      </div>
    </div>
    <FooterBar />
  </div>
</template>

<script>
import HeaderBar from '@/components/HeaderBar.vue';
import FooterBar from '@/components/FooterBar.vue';
import PresetButton from '@/components/ui/PresetButton.vue';
import GenericButton from '@/components/ui/GenericButton.vue';
import PageTitle from '@/components/ui/PageTitle.vue';
import PageDescription from '@/components/ui/PageDescription.vue';

import * as Calculator from '@/utils/calc.js';
import { calculateConfigurationPrice } from '@/utils/utils.js';

export default {
  name: 'NewConfiguration',
  components: {
    HeaderBar,
    FooterBar,
    PresetButton,
    GenericButton,
    PageTitle,
    PageDescription,
  },
  data() {
    return {
      description:
        'Commencer par saisir une largeur de votre choix. Choisissez une configuration parmi les exemples proposés, ou commencer à partir de zéro. ' +
        'Une fois votre configuration sélectionnée, cliquez sur "Utiliser la config. sélectionnée", et commencer la personnalisation de votre dressing. ' +
        "Votre projet est sauvegardé en temps réel sur votre ordinateur, pour pouvoir revenir plus tard sur l'application et terminer votre configuration. " +
        "Une fois votre dressing configuré, cliquez sur le bouton en haut à droite (avec l'enveloppe) pour faire votre demande de rendez-vous magasin.",
      typedWidth: null,
      presets: {
        smallestNumberOfElementsPresets: [],
        largestNumberOfElementsPresets: [],
        lowestCostPresets: [],
        closestWidthPresets: [],
      },
      selectedButtonId: -1,
      selectedConfigurationId: -1,
    };
  },
  methods: {
    launchPresetsCalculation(max, value) {
      // Parse the string values
      max = parseInt(max);
      value = parseInt(value);

      this.presetButtonUnselected();

      if (value <= 0 || value > max || Number.isNaN(value)) {
        return;
      }

      this.presetButtonUnselected();
      this.presets = Calculator.calculatePresets(value);
      this.typedWidth = value;
    },
    hasAtLeastOneConfigurationDefined() {
      let numberOfPresets = 0;

      for (let i = 0; i < 4; i++) {
        numberOfPresets += this.numberOfConfigurationsInPresetAtIndex(i);

        if (numberOfPresets > 0) {
          return true;
        }
      }

      return false;
    },
    /**
     * Returns the number of configurations in the preset defined in a button
     * @param {Number} index The index / ID of the preset button
     */
    numberOfConfigurationsInPresetAtIndex(index = this.selectedButtonId) {
      switch (index) {
        case 0:
          return this.presets.closestWidthPresets.length;
        case 1:
          return this.presets.smallestNumberOfElementsPresets.length;
        case 2:
          return this.presets.largestNumberOfElementsPresets.length;
        case 3:
          return this.presets.lowestCostPresets.length;
      }

      return 0;
    },
    presetButtonSelected(event, buttonId, selectedConfigurationId) {
      this.selectedButtonId = buttonId;
      this.selectedConfigurationId = selectedConfigurationId;
    },
    presetButtonUnselected() {
      this.selectedButtonId = -1;
      this.selectedConfigurationId = -1;
    },
    calcConfigPrice(configuration) {
      return calculateConfigurationPrice(configuration);
    },
    /**
     * Push the configuration selected by the user in the VueX store
     */
    sendPresetConfiguration() {
      for (const keys in this.presets) {
        for (const preset of this.presets[keys]) {
          if (preset.id === this.selectedConfigurationId) {
            this.$store.commit('saveChosenPreset', {
              lastChosenPreset: preset,
            });
            return;
          }
        }
      }
    },
  },
};
</script>

<style scoped>
#configuration-preset-picker,
#buttons-group {
  grid-auto-columns: minmax(0, 1fr);
}
</style>
